import { useCallback, useEffect, useContext } from "react";
import { useServiceCenter } from "../contexts";
import { ServiceCenterAPI } from "../services";
import NotificationsContext from "../../../contexts/NotificationsContext";

/**
 * Hook para gerenciar notas do contato
 */
const useContactNotes = (contactId = null) => {
  const { setNotify } = useContext(NotificationsContext);
  const {
    selectedContact,
    selectedConversation,
    notes,
    loading,
    setNotes,
    addNote,
    updateNote,
    deleteNote: removeNote,
    setLoading,
  } = useServiceCenter();

  // Determinar o ID do contato a usar
  const id = contactId || selectedContact?.id || selectedConversation?.contact_id;

  /**
   * Carregar notas do contato
   */
  const loadNotes = useCallback(async () => {
    if (!id) return;

    setLoading({ notes: true });

    try {
      const response = await ServiceCenterAPI.getNotes(id);
      const notesList = response.results || response || [];
      setNotes(notesList);
    } catch (error) {
      console.error("Erro ao carregar notas:", error);
      setNotes([]);
    } finally {
      setLoading({ notes: false });
    }
  }, [id, setNotes, setLoading]);

  /**
   * Criar nova nota
   */
  const createNote = useCallback(
    async (data) => {
      if (!id) {
        setNotify({
          type: "warning",
          title: "Aviso",
          message: "Contato não encontrado para adicionar nota",
          visible: true,
        });
        return null;
      }

      setLoading({ creating: true });

      try {
        const response = await ServiceCenterAPI.createNote(id, {
          content: data.content,
          note_type: data.note_type || "general",
          is_internal: data.is_internal !== false, // default true
          is_pinned: data.is_pinned || false,
        });

        addNote(response);

        setNotify({
          type: "success",
          title: "Nota Adicionada",
          message: "A nota foi salva com sucesso",
          visible: true,
        });

        return response;
      } catch (error) {
        console.error("Erro ao criar nota:", error);
        setNotify({
          type: "error",
          title: "Erro",
          message: "Não foi possível salvar a nota",
          visible: true,
        });
        return null;
      } finally {
        setLoading({ creating: false });
      }
    },
    [id, addNote, setNotify, setLoading]
  );

  /**
   * Deletar nota
   */
  const deleteNote = useCallback(
    async (noteId) => {
      if (!id || !noteId) return false;

      try {
        await ServiceCenterAPI.deleteNote(id, noteId);
        removeNote(noteId);

        setNotify({
          type: "success",
          title: "Nota Removida",
          message: "A nota foi excluída com sucesso",
          visible: true,
        });

        return true;
      } catch (error) {
        console.error("Erro ao deletar nota:", error);
        setNotify({
          type: "error",
          title: "Erro",
          message: "Não foi possível excluir a nota",
          visible: true,
        });
        return false;
      }
    },
    [id, removeNote, setNotify]
  );

  /**
   * Fixar/Desfixar nota
   */
  const togglePinNote = useCallback(
    async (noteId) => {
      if (!id || !noteId) return null;

      try {
        const response = await ServiceCenterAPI.togglePinNote(id, noteId);

        updateNote({
          id: noteId,
          is_pinned: response.is_pinned,
        });

        setNotify({
          type: "success",
          title: response.is_pinned ? "Nota Fixada" : "Nota Desfixada",
          message: response.is_pinned
            ? "A nota foi fixada no topo"
            : "A nota foi removida do topo",
          visible: true,
        });

        return response;
      } catch (error) {
        console.error("Erro ao fixar nota:", error);
        setNotify({
          type: "error",
          title: "Erro",
          message: "Não foi possível alterar a nota",
          visible: true,
        });
        return null;
      }
    },
    [id, updateNote, setNotify]
  );

  /**
   * Separar notas fixadas das demais
   */
  const getPinnedAndRegularNotes = useCallback(() => {
    const pinnedNotes = notes.filter((note) => note.is_pinned);
    const regularNotes = notes.filter((note) => !note.is_pinned);
    return { pinnedNotes, regularNotes };
  }, [notes]);

  /**
   * Filtrar notas por tipo
   */
  const filterNotesByType = useCallback(
    (type) => {
      if (!type || type === "all") return notes;
      return notes.filter((note) => note.note_type === type);
    },
    [notes]
  );

  // Carregar notas quando o contato mudar
  useEffect(() => {
    if (id) {
      loadNotes();
    } else {
      setNotes([]);
    }
  }, [id]); // Apenas dependência do ID para evitar reloads

  return {
    // State
    notes,
    isLoading: loading.notes,
    isCreating: loading.creating || false,

    // Computed
    ...getPinnedAndRegularNotes(),
    notesCount: notes.length,

    // Actions
    loadNotes,
    createNote,
    deleteNote,
    togglePinNote,
    filterNotesByType,
  };
};

export default useContactNotes;
